import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

const uid = () => Math.random().toString(36).slice(2, 10);
const CUSTOM_PATH = "__custom__";
const CUSTOM_LABEL = "__custom_label__";

function classifyUrl(url, knownPaths) {
  const value = (url || "").trim();
  if (!value) return { ok: false, message: "URL is required" };
  if (value.includes(" ")) return { ok: false, message: "URL cannot contain spaces" };
  if (/^http:\/\//i.test(value)) return { ok: false, message: "Use https:// for external links" };
  if (/^https:\/\//i.test(value)) return { ok: true, kind: "external", message: "External link" };
  if (!value.startsWith("/")) return { ok: false, message: "Use /internal-page or https://…" };
  const path = value.split("?")[0].split("#")[0];
  if (knownPaths.has(path) || path.startsWith("/services/") || path.startsWith("/blog/")) {
    return { ok: true, kind: "internal", known: true, message: "Known public route" };
  }
  return { ok: true, kind: "internal", known: false, message: "Path is not a known public route" };
}

function groupRoutes(routes) {
  const order = ["Pages", "Services", "Blog", "Legal", "Other"];
  const map = {};
  for (const r of routes) {
    const g = r.group || "Other";
    if (!map[g]) map[g] = [];
    map[g].push(r);
  }
  return order.filter((g) => map[g]?.length).map((g) => ({ group: g, items: map[g] }));
}

export default function Navigation() {
  const [menus, setMenus] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [active, setActive] = useState("header");
  const [saved, setSaved] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  const knownPaths = useMemo(() => new Set(routes.map((r) => r.path)), [routes]);
  const pathToLabel = useMemo(() => {
    const m = {};
    for (const r of routes) m[r.path] = r.label;
    return m;
  }, [routes]);
  const labelOptions = useMemo(() => {
    // Unique labels preferring first occurrence (Pages before Services duplicates)
    const seen = new Set();
    const out = [];
    for (const r of routes) {
      const key = (r.label || "").trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [routes]);
  const grouped = useMemo(() => groupRoutes(routes), [routes]);
  const labelGrouped = useMemo(() => groupRoutes(labelOptions), [labelOptions]);

  const fetchData = async () => {
    const { data } = await api.get("/navigation");
    setMenus(data || []);
  };

  const fetchRoutes = async () => {
    setLoadingRoutes(true);
    try {
      const { data } = await api.get("/navigation/routes");
      setRoutes(data?.routes || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load public routes");
      setRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRoutes();
  }, []);

  useEffect(() => {
    const onLeave = (e) => {
      if (saved) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [saved]);

  const current = menus.find((m) => m.location === active) || { location: active, items: [] };

  const setItems = (items) => {
    setSaved(false);
    setMenus((prev) => {
      const found = prev.find((m) => m.location === active);
      if (found) return prev.map((m) => (m.location === active ? { ...m, items } : m));
      return [...prev, { location: active, items }];
    });
  };

  const add = () => {
    const home = routes.find((r) => r.path === "/") || { label: "Home", path: "/" };
    setItems([
      ...(current.items || []),
      { id: uid(), label: home.label, url: home.path, order: (current.items?.length || 0) + 1, children: [] },
    ]);
  };

  const update = (id, patch) =>
    setItems(current.items.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const remove = (id) => setItems(current.items.filter((i) => i.id !== id));

  const move = (idx, dir) => {
    const items = [...current.items];
    const t = idx + dir;
    if (t < 0 || t >= items.length) return;
    [items[idx], items[t]] = [items[t], items[idx]];
    setItems(items.map((i, k) => ({ ...i, order: k + 1 })));
  };

  const moveTo = (from, to) => {
    if (from == null || from === to || from < 0 || to < 0 || to >= current.items.length) return;
    const items = [...current.items];
    const [row] = items.splice(from, 1);
    items.splice(to, 0, row);
    setItems(items.map((i, k) => ({ ...i, order: k + 1 })));
  };

  const pickRouteByPath = (id, path) => {
    if (path === CUSTOM_PATH) {
      update(id, { url: "" });
      return;
    }
    const match = routes.find((r) => r.path === path);
    update(id, {
      url: path,
      label: match?.label || pathToLabel[path] || path,
    });
  };

  const pickRouteByLabel = (id, labelKey) => {
    if (labelKey === CUSTOM_LABEL) {
      update(id, { label: "" });
      return;
    }
    const match = routes.find((r) => r.label === labelKey);
    if (!match) {
      update(id, { label: labelKey });
      return;
    }
    update(id, { label: match.label, url: match.path });
  };

  const urlCounts = (current.items || []).reduce((acc, item) => {
    const key = (item.url || "").trim();
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const invalidCount = (current.items || []).filter(
    (item) => !classifyUrl(item.url, knownPaths).ok || urlCounts[item.url] > 1
  ).length;

  const save = async () => {
    if (invalidCount) {
      toast.error("Fix invalid or duplicate URLs before saving");
      return;
    }
    try {
      await api.patch(`/navigation/${active}`, { items: current.items });
      setSaved(true);
      toast.success("Menu saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Navigation"
        subtitle="Pick known public route names and endpoints from live site pages, services, and blog posts — or enter a custom / external URL."
        actions={
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={fetchRoutes}
              className="text-white/70 hover:text-white hover:bg-white/5"
              disabled={loadingRoutes}
            >
              <Icons.RefreshCw className={`w-4 h-4 mr-1.5 ${loadingRoutes ? "animate-spin" : ""}`} />
              Refresh routes
            </Button>
            <span className={`text-xs ${saved ? "text-emerald-300" : "text-amber-300"}`}>
              {saved ? "All changes saved" : "Unsaved changes"}
            </span>
            <Button onClick={save} className="btn-gradient text-white border-0">
              <Icons.Save className="w-4 h-4 mr-1.5" /> Save menu
            </Button>
          </div>
        }
      />

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="glass border-white/10 mb-6">
          <TabsTrigger value="header" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">
            Header
          </TabsTrigger>
          <TabsTrigger value="footer" className="data-[state=active]:bg-white/10 data-[state=active]:text-white">
            Footer
          </TabsTrigger>
        </TabsList>
        <TabsContent value={active}>
          <GlassCard className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-medium text-white capitalize">{active} menu items</h3>
                <p className="text-[11px] text-white/40 mt-0.5">
                  {loadingRoutes
                    ? "Loading public routes…"
                    : `${routes.length} public endpoints available (pages, services, blog, legal)`}
                </p>
              </div>
              <Button onClick={add} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">
                <Icons.Plus className="w-4 h-4 mr-1.5" /> Add link
              </Button>
            </div>

            <div className="space-y-2">
              {(current.items || []).map((it, idx) => {
                const check = classifyUrl(it.url, knownPaths);
                const duplicate = (urlCounts[it.url] || 0) > 1;
                const pathKnown = knownPaths.has((it.url || "").split("?")[0].split("#")[0]);
                const labelKnown = labelOptions.some((r) => r.label === it.label);
                const pathSelectValue = pathKnown ? it.url.split("?")[0].split("#")[0] : CUSTOM_PATH;
                const labelSelectValue = labelKnown ? it.label : CUSTOM_LABEL;
                const isExternal = /^https:\/\//i.test(it.url || "");

                return (
                  <div
                    key={it.id || `nav-item-${idx}`}
                    draggable
                    onDragStart={() => setDragIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      moveTo(dragIndex, idx);
                      setDragIndex(null);
                    }}
                    className={`p-3 rounded-lg glass space-y-2 ${dragIndex === idx ? "opacity-50" : ""}`}
                  >
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="flex flex-col pt-1">
                        <button type="button" onClick={() => move(idx, -1)} className="p-0.5 text-white/40 hover:text-white" aria-label="Move up">
                          <Icons.ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => move(idx, 1)} className="p-0.5 text-white/40 hover:text-white" aria-label="Move down">
                          <Icons.ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <Icons.GripVertical className="w-4 h-4 text-white/30 cursor-grab mt-2.5" />

                      <div className="w-full sm:w-44 min-w-[10rem] space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-white/40">Route name</p>
                        <Select value={labelSelectValue} onValueChange={(v) => pickRouteByLabel(it.id, v)}>
                          <SelectTrigger className="bg-black/20 border-white/10 text-white h-9">
                            <SelectValue placeholder="Choose name" />
                          </SelectTrigger>
                          <SelectContent className="glass-strong border-white/10 text-white max-h-72">
                            {labelGrouped.map(({ group, items }) => (
                              <SelectGroup key={group}>
                                <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">{group}</SelectLabel>
                                {items.map((r) => (
                                  <SelectItem key={`label-${r.path}`} value={r.label} className="text-white focus:bg-white/10 focus:text-white">
                                    {r.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                            <SelectGroup>
                              <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">Custom</SelectLabel>
                              <SelectItem value={CUSTOM_LABEL} className="text-white focus:bg-white/10 focus:text-white">
                                Custom name…
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {labelSelectValue === CUSTOM_LABEL && (
                          <Input
                            value={it.label}
                            onChange={(e) => update(it.id, { label: e.target.value })}
                            className="bg-black/20 border-white/10 text-white h-9"
                            placeholder="Custom label"
                          />
                        )}
                      </div>

                      <div className="flex-1 min-w-[14rem] space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-white/40">Endpoint</p>
                        <Select value={pathSelectValue} onValueChange={(v) => pickRouteByPath(it.id, v)}>
                          <SelectTrigger className={`bg-black/20 text-white h-9 ${!check.ok || duplicate ? "border-red-400/60" : check.known === false ? "border-amber-400/50" : "border-white/10"}`}>
                            <SelectValue placeholder="Choose /path" />
                          </SelectTrigger>
                          <SelectContent className="glass-strong border-white/10 text-white max-h-72">
                            {grouped.map(({ group, items }) => (
                              <SelectGroup key={group}>
                                <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">{group}</SelectLabel>
                                {items.map((r) => (
                                  <SelectItem key={`path-${r.path}`} value={r.path} className="text-white focus:bg-white/10 focus:text-white">
                                    {`${r.path} — ${r.label}`}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                            <SelectGroup>
                              <SelectLabel className="text-white/45 text-[10px] uppercase tracking-wider">Custom</SelectLabel>
                              <SelectItem value={CUSTOM_PATH} className="text-white focus:bg-white/10 focus:text-white">
                                Custom / or https://…
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        {(pathSelectValue === CUSTOM_PATH || isExternal) && (
                          <Input
                            value={it.url}
                            onChange={(e) => update(it.id, { url: e.target.value })}
                            className={`bg-black/20 text-white h-9 ${!check.ok || duplicate ? "border-red-400/60" : check.known === false ? "border-amber-400/50" : "border-white/10"}`}
                            placeholder="/custom-path or https://example.com"
                          />
                        )}
                      </div>

                      {check.ok && (
                        <a
                          href={it.url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 mt-5 rounded hover:bg-white/5 text-white/60 hover:text-white"
                          aria-label={`Preview ${it.label || "link"}`}
                        >
                          <Icons.ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => remove(it.id)}
                        className="p-2 mt-5 rounded hover:bg-red-500/10 text-white/60 hover:text-red-300"
                        aria-label="Remove link"
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className={`text-[11px] pl-12 ${!check.ok || duplicate ? "text-red-300" : check.known === false ? "text-amber-300" : "text-white/40"}`}>
                      {duplicate ? "Duplicate URL in this menu. " : ""}
                      {check.message}
                      {pathKnown ? ` · ${it.url}` : ""}
                    </p>
                  </div>
                );
              })}
              {(current.items || []).length === 0 && (
                <div className="text-center text-sm text-white/40 py-8">No items. Click &quot;Add link&quot; to create one.</div>
              )}
            </div>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
